import type { JSX } from "react"
import { createRoot } from "react-dom/client"
import ResizeObserverPolyfill from "resize-observer-polyfill"

import {
  initSentry,
  setMonitorPlatform,
  startHealthMonitor,
  trackJourney,
  type MonitorBootstrapConfig,
} from "@be-miniapp/logging"

interface IBootstrap {
  monitor?: MonitorBootstrapConfig
  renderApp?: () => JSX.Element | null
}

export async function bootstrap({
  monitor = {
    enabled: false,
  },
  renderApp = () => null,
}: IBootstrap) {
  if (monitor.platform) {
    setMonitorPlatform(monitor.platform)
  }

  if (monitor.enabled) {
    if (!monitor.config) {
      throw new Error("Sentry config is required when monitor is enabled")
    }
    await initSentry(monitor.config)
    if (monitor.platform) {
      setMonitorPlatform(monitor.platform)
    }
    startHealthMonitor()
  }

  trackJourney("app_launch", "start")

  if (monitor.config?.eruda) {
    import("eruda").then((eruda) => {
      eruda.default.init()
    })
  }

  try {
    await initializePolyfills()

    const root = createRoot(document.getElementById("app") as HTMLElement)
    root.render(renderApp())

    trackJourney("app_launch", "success")
  } catch (error) {
    trackJourney("app_launch", "fail", {
      reason: error instanceof Error ? error.message : "bootstrap_failed",
    })
    throw error
  }
}

async function initializePolyfills() {
  const viewport = document.querySelector("meta[name=viewport]")

  if (!Promise.race) {
    Promise.race = (promises: Promise<any>[]) =>
      new Promise((resolve, reject) => {
        Promise.all(promises).then(resolve).catch(reject)
      })
  }

  if (!window.ResizeObserver) {
    window.ResizeObserver = ResizeObserverPolyfill
  }

  if (viewport) {
    viewport.setAttribute(
      "content",
      "width=device-width, initial-scale=1, maximum-scale=1, minimum-scale=1, user-scalable=no, viewport-fit=cover, interactive-widget:resizes-content"
    )
  }
}
